import {SkeletonAvatar, SkeletonText, Stack} from '@primer/react/experimental'
import {CommentLayout} from './CommentLayout'
import {CommentsEmptyState} from './CommentsEmptyState'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'

/**
 * A single thread preview skeleton that mimics the ThreadPreview component structure
 */
function CommentsSkeletonItem({lines}: {lines: number}) {
  return (
    <CommentLayout
      data-testid="thread-preview-skeleton-item"
      header={
        <Stack direction="horizontal" align="center" gap="condensed">
          <SkeletonAvatar size={24} />
          <SkeletonText size="bodyMedium" />
        </Stack>
      }
    >
      <SkeletonText size="bodyMedium" lines={lines} />
    </CommentLayout>
  )
}

const lineCounts = [1, 3, 2] // repeating

/** Shown when we don't know how many comments to expect. */
const defaultSkeletonCount = 3

/** More skeletons than this would never fit on screen, so rendering them is wasted work. */
const maxSkeletonCount = 15

/**
 * Renders skeleton loading states for thread previews.
 * Used as a fallback while thread preview data is being fetched.
 */
export function CommentsSkeleton({reviewCommentsMode}: {reviewCommentsMode: boolean}) {
  // If we have comments count data, we can show a more accurate skeleton. But if we don't have it it's not a big deal
  // and we don't want to execute the query just for this if we don't need the data elsewhere, so it's not enabled.
  const commentsCount =
    useRouteQuery(pullRequestsLayoutRoute, 'tabCounts', {enabled: false})?.data?.conversationCount ??
    defaultSkeletonCount

  return commentsCount > 0 ? (
    <Stack gap="normal" data-testid="thread-preview-skeleton">
      <p className="sr-only">Loading comments...</p>
      {Array.from({length: Math.min(commentsCount, maxSkeletonCount)}).map((_, i) => (
        <CommentsSkeletonItem key={i} lines={lineCounts[i % lineCounts.length] ?? 2} />
      ))}
    </Stack>
  ) : (
    <CommentsEmptyState reviewCommentsMode={reviewCommentsMode} />
  )
}
